/**
 * Общее у списков блока «Голоса клиента» (`VoiceTopList`): размеры, пропсы и
 * свёрнутые строки — для двух видов списка, свёрнутого и постраничного.
 */
import { useEffect, type ReactNode } from 'react';

/** Сколько строк блока видно, пока он не развёрнут. */
export const VOICE_TOP_ROWS = 5;

/** Строк на странице блока «Маркетинга» и «Продукта» (#2314). */
export const VOICE_PAGE_ROWS = 10;

/** Строки, видимые свёрнутыми: первые `limit` из тех, где разговоры есть. */
export function collapsedRows<T>(
  rows: readonly T[],
  limit: number,
  calls: (row: T) => number
): T[] {
  return rows.filter((row) => calls(row) > 0).slice(0, limit);
}

/** Сообщить, какие строки на экране (`onShownKeys`) — после смены их набора. */
export function useShownKeys<T>(
  visible: readonly T[],
  rowKey: (row: T) => string,
  onShownKeys: ((keys: readonly string[]) => void) | undefined
): void {
  const mark = JSON.stringify(visible.map(rowKey));
  useEffect(() => {
    onShownKeys?.(JSON.parse(mark) as string[]);
  }, [mark, onShownKeys]);
}

export interface VoiceTopListProps<T> {
  rows: readonly T[];
  /** Свёрнутый размер; нет — все строки, без кнопки. */
  limit?: number;
  /** Строк на странице — перелистывание вместо «Показать все»; `limit` тогда не читается. */
  pageSize?: number;
  /** The block's name for the pager's landmark: two paged blocks are told apart by it. */
  label?: string;
  rowKey: (row: T) => string;
  /** Нулевые строки уходят под «Показать все»; нет — нулей у блока не бывает. */
  calls?: (row: T) => number;
  /**
   * Строки, которые нельзя прятать за пятёркой: раскрытая по адресу и
   * выбранная темой (#2229). Любая за пятёркой — список развёрнут; со
   * страницами — открыта страница строки, которую только что открыли или сдвинули.
   */
  revealKeys?: readonly (string | null | undefined)[];
  /** The row's element, keyed by `rowKey`. */
  renderRow: (row: T) => ReactNode;
  /**
   * The keys of the rows on screen, after each change of them: a block asks
   * the series of these (#2394 §9 п. 7), not of rows folded under «Show all»
   * or on another page.
   */
  onShownKeys?: (keys: readonly string[]) => void;
  /** `ul` when the rows are `li`. */
  list?: 'div' | 'ul';
  /** The gap between the rows. */
  gap?: 'space-y-1' | 'space-y-2';
}
